import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles'

import { Text } from '@mxenabled/mxui'

import { ReactComponent as Left } from './shared/images/SavingsOpportunityLeft.svg'
import { ReactComponent as LeftDark } from './shared/images/SavingsOpportunityLeftDark.svg'
import { ReactComponent as Perforation } from './shared/images/SavingsOpportunityPerforation.svg'

/**
 * Figma slot: a white base layered with a `success.main → success.light` linear
 * gradient at 75% opacity (a green wash left-to-right). Both tokens are
 * theme-aware so the tint flips for dark mode. Matches the "backgroundImage over
 * bgcolor" pattern used across the insights-v2 Content components.
 */
const SLOT_GRADIENT_OPACITY = 0.75

interface SavingsOpportunityCardProps {
  amount: string
  label: string
  /** Whether to render the green gradient slot behind the coupon. Default true. */
  slot?: boolean
  sx?: SxProps<Theme>
}

/**
 * Savings Opportunity V2 content: a coupon (green wallet tile + perforation +
 * amount) on a green gradient slot. Ported from the v1 `Coupon` component
 * (`src/insights/components/shared/Coupon.tsx`) and adapted to the v2 theme.
 *
 * Figma: Savings Opportunity V2 Content (Full variant, coupon-style).
 * Scope is the Content section only.
 */
const SavingsOpportunityCard = ({
  amount,
  label,
  slot = true,
  sx,
}: SavingsOpportunityCardProps) => {
  const theme = useTheme()
  const slotBackground = `linear-gradient(90deg, ${alpha(
    theme.palette.success.main,
    SLOT_GRADIENT_OPACITY,
  )}, ${alpha(theme.palette.success.light, SLOT_GRADIENT_OPACITY)})`

  return (
    <Box
      sx={{
        backgroundImage: slot ? slotBackground : undefined,
        bgcolor: slot ? 'background.paper' : 'transparent',
        p: 16,
        ...sx,
      }}
    >
      <Stack sx={{ flexDirection: 'row', justifyContent: 'center' }}>
        <Stack
          sx={{
            bgcolor: 'background.paper',
            borderBottomLeftRadius: '8px',
            borderTopLeftRadius: '8px',
            p: 4,
            pr: 2,
          }}
        >
          {theme.palette.mode === 'dark' ? (
            <LeftDark aria-hidden={true} />
          ) : (
            <Left aria-hidden={true} />
          )}
        </Stack>
        <Stack
          sx={{
            svg: {
              path: {
                '&:not([stroke])': {
                  fill: theme.palette.background.paper,
                },
                '&[stroke]': {
                  stroke: alpha(theme.palette.divider, 0.24),
                },
              },
              rect: {
                fill: theme.palette.background.paper,
              },
            },
          }}
        >
          <Perforation aria-hidden={true} />
        </Stack>
        <Stack
          sx={{
            bgcolor: 'background.paper',
            borderBottomRightRadius: '12px',
            borderTopRightRadius: '12px',
            height: 74,
            justifyContent: 'center',
            px: 12,
            width: 216,
          }}
        >
          <Text color="text.secondary" variant="caption">
            {label}
          </Text>
          <Text variant="h1">{amount}</Text>
        </Stack>
      </Stack>
    </Box>
  )
}

export default SavingsOpportunityCard
